// ============================================
// HOOK: useEquipments - CORREGIDO
// ============================================
// Archivo: src/hooks/useEquipments.js

import { useState, useEffect, useCallback } from 'react';
import { equipmentService } from '../services/api';
import { mapEquipmentFromBackend } from '../utils/mappers';
import { toast } from 'react-hot-toast';

const validateEquipment = (equipmentData) => {
  const errors = [];

  if (!equipmentData.name || !equipmentData.name.trim()) {
    errors.push('El nombre es obligatorio');
  }

  if (!equipmentData.plant_id) {
    errors.push('La planta es obligatoria');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const useEquipments = (isAuthenticated) => {
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadEquipments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await equipmentService.getAll();
      const mapped = data.map(mapEquipmentFromBackend);
      setEquipments(mapped);
    } catch (err) {
      setError(err.message);
      toast.error('Error cargando equipos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadEquipments();
    }
  }, [isAuthenticated, loadEquipments]);

  const createEquipment = useCallback(async (equipmentData) => {
    console.log('📤 Datos recibidos en createEquipment:', equipmentData);
    
    // Validar
    const validation = validateEquipment(equipmentData);
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return { success: false, errors: validation.errors };
    }

    setLoading(true);
    try {
      // ✅ ENVIAR DIRECTO - El modal ya envía en formato backend
      console.log('📤 Enviando al backend:', equipmentData);
      const created = await equipmentService.create(equipmentData);
      console.log('📥 Respuesta del backend:', created);
      
      const mapped = mapEquipmentFromBackend(created);
      
      setEquipments(prev => [...prev, mapped]);
      toast.success('Equipo creado correctamente');
      
      return { success: true, equipment: mapped };
    } catch (err) {
      console.error('❌ Error completo:', err);
      toast.error(err.message || 'Error creando equipo');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateEquipment = useCallback(async (id, equipmentData) => {
    console.log('📝 Actualizando equipo:', id, equipmentData);
    
    // Validar
    const validation = validateEquipment(equipmentData);
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return { success: false, errors: validation.errors };
    }

    setLoading(true);
    try {
      // ✅ ENVIAR DIRECTO - El modal ya envía en formato backend
      const updated = await equipmentService.update(id, equipmentData);
      const mapped = mapEquipmentFromBackend(updated);
      
      setEquipments(prev => prev.map(e => e.id === id ? mapped : e));
      toast.success('Equipo actualizado correctamente');
      
      return { success: true, equipment: mapped };
    } catch (err) {
      console.error('❌ Error actualizando:', err);
      toast.error(err.message || 'Error actualizando equipo');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteEquipment = useCallback(async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este equipo?')) {
      return { success: false, cancelled: true };
    }

    setLoading(true);
    try {
      await equipmentService.delete(id);
      setEquipments(prev => prev.filter(e => e.id !== id));
      toast.success('Equipo eliminado correctamente');
      return { success: true };
    } catch (err) {
      toast.error(err.message || 'Error eliminando equipo');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const getEquipmentsByPlant = useCallback((plantId) => {
    return equipments.filter(e => e.plantId === plantId);
  }, [equipments]);

  const getEquipmentsByLine = useCallback((plantId, line) => {
    return equipments.filter(e => e.plantId === plantId && e.line === line);
  }, [equipments]);

  return {
    equipments,
    loading,
    error,
    loadEquipments,
    createEquipment,
    updateEquipment,
    deleteEquipment,
    getEquipmentsByPlant,
    getEquipmentsByLine
  };
};